import { useCallback, useMemo, useRef, useState } from 'react';
import { FormInstance } from 'antd';
import { useLazyQuery } from '@apollo/client';
import { isRealHts } from '@xbcb/entry-utils/dist/lib/htsUtil';
import type { NamePath } from '@xbcb/ui-types';
import type { TariffEvaluatorOutput as SharedTariffEvaluatorOutput } from '@xbcb/api-gateway-client';
import {
  evaluateTariffsDocument,
  ModeOfTransport,
  type TariffEvaluatorInput,
} from '__generated__/graphql';
import { applyTariffEvaluationToFormLine } from 'libs/applyTariffEvaluation';
import { isTariff232AutomationFeatureEnabled } from 'featureFlags';

interface UseTariffEvaluationArgs {
  form: FormInstance;
  tariffFullNamePath: NamePath;
  originCountryCode: string | undefined;
  arrivalDate: string | undefined;
  modeOfTransport: ModeOfTransport | undefined;
}

export const useTariffEvaluation = ({
  form,
  tariffFullNamePath,
  originCountryCode,
  arrivalDate,
  modeOfTransport,
}: UseTariffEvaluationArgs) => {
  const [evaluateTariffs] = useLazyQuery(evaluateTariffsDocument);
  const [evaluating, setEvaluating] = useState(false);

  const { lineFullNamePath, productFullNamePath, linesPath, lineIndex } =
    useMemo(() => {
      const path = tariffFullNamePath as (string | number)[];
      const linePath = path.slice(0, -1);
      const linesIdx = path.lastIndexOf('lines');
      const linesArrayPath =
        linesIdx >= 0 ? path.slice(0, linesIdx + 1) : linePath;
      const productPath = linesIdx >= 0 ? path.slice(0, linesIdx) : linePath;
      const idx = linesIdx >= 0 ? (path[linesIdx + 1] as number) : 0;
      return {
        lineFullNamePath: linePath,
        productFullNamePath: productPath,
        linesPath: linesArrayPath,
        lineIndex: idx,
      };
    }, [tariffFullNamePath]);

  const argsRef = useRef({ originCountryCode, arrivalDate, modeOfTransport });
  argsRef.current = { originCountryCode, arrivalDate, modeOfTransport };
  const inflightRef = useRef(false);

  const runTariffEvaluation = useCallback(
    async (htsCode: string) => {
      const { originCountryCode, arrivalDate, modeOfTransport } =
        argsRef.current;
      if (!isRealHts(htsCode) || !originCountryCode) return;
      if (inflightRef.current) return;

      const line = form.getFieldValue(lineFullNamePath) ?? {};
      const product = form.getFieldValue(productFullNamePath) ?? {};
      const transport =
        arrivalDate || modeOfTransport
          ? {
              ...(arrivalDate && { arrival: { importDate: arrivalDate } }),
              ...(modeOfTransport && { modeOfTransport }),
            }
          : undefined;

      setEvaluating(true);
      inflightRef.current = true;
      try {
        let candidateAdditionalTariffs:
          | SharedTariffEvaluatorOutput['candidateAdditionalTariffs']
          | undefined;
        const newLines = await applyTariffEvaluationToFormLine({
          line,
          product,
          transport,
          evaluate: async (input) => {
            const { data } = await evaluateTariffs({
              variables: { input: input as unknown as TariffEvaluatorInput },
            });
            const result = data?.evaluateTariffs as
              | SharedTariffEvaluatorOutput
              | null
              | undefined;
            candidateAdditionalTariffs = result?.candidateAdditionalTariffs;
            return result;
          },
        });

        if (isTariff232AutomationFeatureEnabled) {
          if (candidateAdditionalTariffs?.length) {
            form.setFieldValue(
              [...lineFullNamePath, 'candidateAdditionalTariffs'],
              candidateAdditionalTariffs.map(({ number, type }) => ({
                number,
                type,
              })),
            );
            return;
          }

          form.setFieldValue(
            [...lineFullNamePath, 'candidateAdditionalTariffs'],
            [],
          );
        }

        if (!newLines?.length) return;

        if (newLines.length === 1) {
          form.setFieldValue(lineFullNamePath, newLines[0]);
        } else {
          const allLines =
            (form.getFieldValue(linesPath) as unknown[] | undefined) ?? [];
          form.setFieldValue(linesPath, [
            ...allLines.slice(0, lineIndex),
            ...newLines,
            ...allLines.slice(lineIndex + 1),
          ]);
        }
      } catch {
        // Tariff evaluation failed — additional tariffs won't auto-populate.
      } finally {
        inflightRef.current = false;
        setEvaluating(false);
      }
    },
    [
      evaluateTariffs,
      form,
      lineFullNamePath,
      productFullNamePath,
      linesPath,
      lineIndex,
    ],
  );

  return { runTariffEvaluation, evaluating };
};
